import { addEntity, createWorld, IWorld } from "bitecs";
import "./aframe-to-bit-components";
import { AEntity, Networked, Object3DTag, Owned } from "./bit-components";
import MediaSearchStore from "./storage/media-search-store";
import Store from "./storage/store";
import qsTruthy from "./utils/qs_truthy";

import type { AComponent, AScene } from "aframe";
import HubChannel from "./utils/hub-channel";
import MediaDevicesManager from "./utils/media-devices-manager";

import { EffectComposer, EffectPass } from "postprocessing";
import {
  Audio,
  AudioListener,
  Material,
  Object3D,
  PerspectiveCamera,
  PositionalAudio,
  Scene,
  SRGBColorSpace,
  WebGLRenderer
} from "three";
import { AudioSettings, SourceType } from "./components/audio-params";
import { createEffectsComposer } from "./effects";
// import { DialogAdapter} from "./agora-dialog-adapter";
import { DialogAdapter } from "./naf-dialog-adapter";
import { mainTick } from "./systems/hubs-systems";
import { waitForPreloads } from "./utils/preload";
import SceneEntryManager from "./scene-entry-manager";
import { store } from "./utils/store-instance";
import { addObject3DComponent } from "./utils/jsx-entity";
import { ElOrEid } from "./utils/bit-utils";
import MediaDeviceManagerPermissions from "./singleton/MediaDeviceManagerPermissions";
import { XRControllerModelFactory } from "three/examples/jsm/webxr/XRControllerModelFactory.js";
import { XRHandModelFactory } from "./utils/XRHandModelFactory.js";
import { OculusHandPointerModel } from "three/examples/jsm/webxr/OculusHandPointerModel.js";
import { Layers } from "./camera-layers";
import { applyWebXREmulatorCompat, getWebXRConfigWithoutLayers } from "./utils/webxr-emulator-compat";
import { updateAudioSettings } from "./update-audio-settings";

const DEFAULT_CONTROLLERS_PATH = "https://mudfoundation.blob.core.windows.net/controllers";
const DEFAULT_HANDS_PATH = "https://mudfoundation.blob.core.windows.net/controllers/generic-hand/";
const MAIN_SCENE_INTERACTION_SUPPRESSION_STATE = "in-modal-or-overlay";

declare global {
  interface Window {
    $O: (eid: number) => Object3D | undefined;
    APP: App;
  }
  const APP: App;
}

export interface HubsWorld extends IWorld {
  scene: Scene;
  nameToComponent: {
    object3d: typeof Object3DTag;
    networked: typeof Networked;
    owned: typeof Owned;
    AEntity: typeof AEntity;
  };
  ignoredNids: Set<number>;
  deletedNids: Set<number>;
  nid2eid: Map<number, number>;
  eid2obj: Map<number, Object3D>;
  eid2mat: Map<number, Material>;
  time: { delta: number; elapsed: number; tick: number };
}

let resolvePromiseToScene: (value: Scene) => void;
const promiseToScene: Promise<Scene> = new Promise(resolve => {
  resolvePromiseToScene = resolve;
});
export function getScene() {
  return promiseToScene;
}

interface HubDescription {
  hub_id: string;
}

export class App {
  scene?: AScene;
  hubChannel?: HubChannel;
  hub?: HubDescription;
  mediaDevicesManager?: MediaDevicesManager;
  mediaDevicesManagerPermissions?: MediaDeviceManagerPermissions;
  entryManager?: SceneEntryManager;
  messageDispatch?: any;
  store: Store;
  componentRegistry: { [key: string]: AComponent[] };

  mediaSearchStore = new MediaSearchStore();

  audios = new Map<ElOrEid, PositionalAudio | Audio>();
  sourceType = new Map<ElOrEid, SourceType>();
  audioOverrides = new Map<ElOrEid, Partial<AudioSettings>>();
  zoneOverrides = new Map<ElOrEid, Partial<AudioSettings>>();
  gainMultipliers = new Map<ElOrEid, number>();
  supplementaryAttenuation = new Map<ElOrEid, number>();
  clippingState = new Set<ElOrEid>();
  mutedState = new Set<ElOrEid>();
  isAudioPaused = new Set<ElOrEid>();
  audioDebugPanelOverrides = new Map<SourceType, Partial<AudioSettings>>();
  sceneAudioDefaults = new Map<SourceType, Partial<AudioSettings>>();
  moderatorAudioSource = new Set<ElOrEid>();
  participantLayoutFlatVoiceAudioEnabled = false;

  world: HubsWorld = createWorld();

  str2sid: Map<string | null, number>;
  sid2str: Map<number, string | null>;
  nextSid = 1;

  audioListener: AudioListener;

  dialog = new DialogAdapter();

  RENDER_ORDER = {
    HUD_BACKGROUND: 1,
    HUD_ICONS: 2,
    CURSOR: 3
  };

  fx: {
    composer?: EffectComposer;
    bloomAndTonemapPass?: EffectPass;
    tonemapOnlyPass?: EffectPass;
  } = {};

  // Add renderer management properties
  mainRenderer?: WebGLRenderer;
  mainAnimationLoop?: (rafTime: number, xrFrame: XRFrame) => void;
  isMainRendererPaused = false;
  mainSceneRenderSuppressed = false;
  mainSceneRenderSuppressionSources = new Set<string>();
  mainSceneInteractionSuppressionSources = new Set<string>();

  constructor() {
    this.store = store;
    // TODO: Create accessor / update methods for these maps / set
    this.world.eid2obj = new Map();
    this.world.eid2mat = new Map();

    this.world.nid2eid = new Map();
    this.world.deletedNids = new Set();
    this.world.ignoredNids = new Set();

    // used in aframe and networked aframe to avoid imports
    this.world.nameToComponent = {
      object3d: Object3DTag,
      networked: Networked,
      owned: Owned,
      AEntity
    };

    // reserve entity 0 to avoid needing to check for undefined everywhere eid is checked for existance
    addEntity(this.world);

    this.str2sid = new Map([[null, 0]]);
    this.sid2str = new Map([[0, null]]);

    window.$O = eid => this.world.eid2obj.get(eid);
  }

  // Add methods to pause/resume main renderer
  pauseMainRenderer() {
    if (this.mainRenderer && !this.isMainRendererPaused) {
      this.mainRenderer.setAnimationLoop(null);
      this.isMainRendererPaused = true;
    }
  }

  resumeMainRenderer() {
    if (this.mainRenderer && this.isMainRendererPaused && this.mainAnimationLoop) {
      this.mainRenderer.setAnimationLoop(this.mainAnimationLoop);
      this.isMainRendererPaused = false;
    }
  }

  setMainSceneRenderSuppressed(suppressed: boolean, source = "default") {
    const suppressionSource = source || "default";

    if (suppressed) {
      this.mainSceneRenderSuppressionSources.add(suppressionSource);
    } else {
      this.mainSceneRenderSuppressionSources.delete(suppressionSource);
    }

    this.mainSceneRenderSuppressed = this.mainSceneRenderSuppressionSources.size > 0;
  }

  setMainSceneInteractionSuppressed(suppressed: boolean, source = "default") {
    const suppressionSource = source || "default";
    const wasSuppressed = this.mainSceneInteractionSuppressionSources.size > 0;

    if (suppressed) {
      this.mainSceneInteractionSuppressionSources.add(suppressionSource);
    } else {
      this.mainSceneInteractionSuppressionSources.delete(suppressionSource);
    }

    const isSuppressed = this.mainSceneInteractionSuppressionSources.size > 0;

    if (isSuppressed === wasSuppressed) {
      return;
    }

    if (isSuppressed) {
      (this.scene as any)?.addState?.(MAIN_SCENE_INTERACTION_SUPPRESSION_STATE);
    } else {
      (this.scene as any)?.removeState?.(MAIN_SCENE_INTERACTION_SUPPRESSION_STATE);
    }
  }

  setParticipantLayoutFlatVoiceAudioEnabled(enabled: boolean) {
    const nextEnabled = Boolean(enabled);

    if (this.participantLayoutFlatVoiceAudioEnabled === nextEnabled) {
      return;
    }

    this.participantLayoutFlatVoiceAudioEnabled = nextEnabled;

    for (const [elOrEid, audio] of this.audios.entries()) {
      if (this.sourceType.get(elOrEid) !== SourceType.AVATAR_AUDIO_SOURCE) {
        continue;
      }

      this.supplementaryAttenuation.delete(elOrEid);
      this.clippingState.delete(elOrEid);
      updateAudioSettings(elOrEid, audio);
    }
  }

  // TODO nothing ever cleans these up
  getSid(str: string) {
    if (!this.str2sid.has(str)) {
      const sid = this.nextSid;
      this.nextSid = this.nextSid + 1;
      this.str2sid.set(str, sid);
      this.sid2str.set(sid, str);
      return sid;
    }
    return this.str2sid.get(str)!;
  }

  getString(sid: number) {
    return this.sid2str.get(sid);
  }

  setupHandsAndControllers(renderer: WebGLRenderer, sceneEl: AScene) {
    const xrCompat = applyWebXREmulatorCompat(window);
    const webxrData = sceneEl.getAttribute("webxr");

    if (webxrData) {
      const webxrDataObj = {
        ...(webxrData as unknown as any),
        optionalFeatures: [...(((webxrData as unknown as any).optionalFeatures || []) as string[])]
      };
      let shouldUpdateWebXRConfig = false;

      if (xrCompat.disabledNativeXRWebGLBinding) {
        const compatWebXRConfig = getWebXRConfigWithoutLayers(webxrDataObj);

        if (compatWebXRConfig.optionalFeatures.length !== webxrDataObj.optionalFeatures.length) {
          webxrDataObj.optionalFeatures = compatWebXRConfig.optionalFeatures;
          shouldUpdateWebXRConfig = true;
          console.warn("Disabled XRWebGLBinding and removed WebXR layers for emulator compatibility.");
        }
      }

      const optionalFeaturesArray = webxrDataObj.optionalFeatures;
      if (optionalFeaturesArray.indexOf("hand-tracking") === -1) {
        optionalFeaturesArray.push("hand-tracking");
        shouldUpdateWebXRConfig = true;
      }

      if (shouldUpdateWebXRConfig) {
        sceneEl.setAttribute("webxr", webxrDataObj);
      }
    }

    let avatarRig = (document.querySelector("#viewing-camera") as unknown as { object3D: Object3D }).object3D;
    let hand1, hand2;
    let controller1: THREE.Object3D, controller2: THREE.Object3D;
    let controllerGrip1, controllerGrip2;

    controller1 = renderer.xr.getController(0);

    const geometry = new THREE.CylinderGeometry(0, 0.05, 0.2, 32).rotateX(Math.PI / 2);

    function onSelect() {
      const material = new THREE.MeshPhongMaterial({ color: 0xffffff * Math.random() });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(0, 0, -0.3).applyMatrix4(controller1.matrixWorld);
      mesh.quaternion.setFromRotationMatrix(controller1.matrixWorld);
      sceneEl?.object3D.add(mesh);
    }

    // controller1.addEventListener( 'select', onSelect );
    avatarRig.add(controller1);

    controller2 = renderer.xr.getController(1);
    // controller2.addEventListener( 'select', onSelect );
    avatarRig.add(controller2);

    const controllerModelFactory = new XRControllerModelFactory();
    // @ts-ignore
    controllerModelFactory.setPath(DEFAULT_CONTROLLERS_PATH);
    const handModelFactory = new XRHandModelFactory();
    // @ts-ignore
    handModelFactory.setPath(DEFAULT_HANDS_PATH);

    controllerGrip1 = renderer.xr.getControllerGrip(0);
    controllerGrip1.add(controllerModelFactory.createControllerModel(controllerGrip1));
    avatarRig.add(controllerGrip1);

    hand1 = renderer.xr.getHand(0);
    hand1.add(handModelFactory.createHandModel(hand1, "mesh"));
    const handPointer1 = new OculusHandPointerModel(hand1, controller1);
    hand1.add(handPointer1);
    avatarRig.add(hand1);

    controllerGrip2 = renderer.xr.getControllerGrip(1);
    controllerGrip2.add(controllerModelFactory.createControllerModel(controllerGrip2));
    avatarRig.add(controllerGrip2);

    hand2 = renderer.xr.getHand(1);
    hand2.add(handModelFactory.createHandModel(hand2, "mesh"));
    const handPointer2 = new OculusHandPointerModel(hand2, controller2);
    hand2.add(handPointer2);
    avatarRig.add(hand2);

    controller1.addEventListener("connected", event => {
      const xrInputSource = event.data;
      if (xrInputSource.handedness) {
        handPointer1.name = xrInputSource.handedness;
        (window as any).APP.handPointer1 = handPointer1;
      }
    });

    controller2.addEventListener("connected", event => {
      const xrInputSource = event.data;
      if (xrInputSource.handedness) {
        handPointer2.name = xrInputSource.handedness;
        (window as any).APP.handPointer2 = handPointer2;
      }
    });

    const setObjectLayers = (obj: THREE.Object3D, uiLayer: number, fxLayer: number) => {
      obj.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.layers.set(uiLayer);
          child.layers.enable(fxLayer);
        }
      });
    };

    const UI_LAYER = Layers.CAMERA_LAYER_UI;
    const FX_MASK_LAYER = Layers.CAMERA_LAYER_FX_MASK;

    setObjectLayers(controllerGrip1, UI_LAYER, FX_MASK_LAYER);
    setObjectLayers(controllerGrip2, UI_LAYER, FX_MASK_LAYER);
    setObjectLayers(hand1, UI_LAYER, FX_MASK_LAYER);
    setObjectLayers(hand2, UI_LAYER, FX_MASK_LAYER);
    setObjectLayers(handPointer1, UI_LAYER, FX_MASK_LAYER);
    setObjectLayers(handPointer2, UI_LAYER, FX_MASK_LAYER);

    (window as any).APP.handPointers = [handPointer1, handPointer2];
  }

  // This gets called by a-scene to setup the renderer, camera, and audio listener
  // TODO ideally the control flow here would be inverted, and we would setup this stuff,
  // initialize aframe, and then run our own RAF loop
  setupRenderer(sceneEl: AScene) {
    const canvas = document.createElement("canvas");
    canvas.classList.add("a-canvas");
    canvas.dataset.aframeCanvas = "true";

    // TODO this comes from aframe and prevents zoom on ipad.
    // This should alreeady be handleed by disable-ios-zoom but it does not appear to work
    canvas.addEventListener("touchmove", function (event) {
      event.preventDefault();
    });

    // Handle WebGL context loss to prevent app crash and allow recovery
    canvas.addEventListener(
      "webglcontextlost",
      event => {
        event.preventDefault();
        console.warn("[App] Main WebGL Context Lost! Pausing renderer...");
        this.pauseMainRenderer();
      },
      false
    );

    canvas.addEventListener(
      "webglcontextrestored",
      () => {
        console.log("[App] Main WebGL Context Restored! Resuming renderer...");
        this.resumeMainRenderer();
        // Trigger a meaningful re-render or scene refresh if necessary
        if (this.scene) {
          // A-Frame/Three.js usually handles the internal restoration of resources if handleContextRestored is called
           // forcing a resize/render might be good
           this.mainRenderer?.setSize(window.innerWidth, window.innerHeight, false);
        }
      },
      false
    );

    // const enablePostEffects = this.store.state.preferences.enablePostEffects;
    const enablePostEffects = false;

    const renderer = new WebGLRenderer({
      alpha: true,
      antialias: !enablePostEffects,
      depth: !enablePostEffects,
      stencil: false,
      powerPreference: "high-performance",
      canvas,
      preserveDrawingBuffer: false
    });

    // We manually handle resetting this in mainTick so that stats are correctly reported with post effects enabled
    renderer.info.autoReset = false;

    renderer.setPixelRatio(window.devicePixelRatio);

    renderer.debug.checkShaderErrors = qsTruthy("checkShaderErrors");

    // These get overridden by environment-system but setting to the highly expected defaults to avoid any extra work
    // @ts-ignore
    renderer.outputColorSpace = SRGBColorSpace;

    sceneEl.appendChild(renderer.domElement);

    const camera = new PerspectiveCamera(80, window.innerWidth / window.innerHeight, 0.05, 10000);

    const audioListener = new AudioListener();
    this.audioListener = audioListener;
    const audioListenerEid = addEntity(this.world);
    addObject3DComponent(this.world, audioListenerEid, this.audioListener);

    camera.add(audioListener);

    this.world.time = {
      delta: 0,
      elapsed: 0,
      tick: 0
    };

    this.scene = sceneEl;
    const scene = sceneEl.object3D;
    this.world.scene = scene;
    resolvePromiseToScene(scene);

    // We manually call scene.updateMatrixWolrd in mainTick
    // @ts-ignore scene.matrixWorldAutoUpdate doesn't exist on 'Scene'
    scene.matrixWorldAutoUpdate = false;

    if (enablePostEffects) {
      this.fx = createEffectsComposer(canvas, renderer, camera, scene, sceneEl, this.store);
    } else {
      // EffectComposer manages renderer size internally
      (sceneEl as any).addEventListener("rendererresize", function ({ detail }: { detail: DOMRectReadOnly }) {
        renderer.setSize(detail.width, detail.height, false);
      });
    }

    // This gets called after all system and component init functions
    sceneEl.addEventListener("loaded", () => {
      waitForPreloads().then(() => {
        this.world.time.elapsed = performance.now();

        // Store the animation loop function for later use
        this.mainAnimationLoop = function (_rafTime, xrFrame) {
          mainTick(xrFrame, renderer, scene, camera);
        };

        renderer.setAnimationLoop(this.mainAnimationLoop);

        sceneEl.renderStarted = true;

        this.setupHandsAndControllers(renderer, sceneEl);
      });
    });

    // Store renderer reference
    this.mainRenderer = renderer;

    return {
      renderer,
      camera,
      audioListener
    };
  }
}
